import gqlClient from '@/modules/graphql'
import { messageRead } from '@/store/modules/dashboard/queries.graphql'
import * as types from './mutation-type'
import api from '@/modules/api'

const CACHE_TTL = 60000
const CACHE_PREFIX = 'splus:notifications:v1'
let inFlight = null
let queuedRefreshKey = null

const readCache = cacheKey => {
  try {
    const cached = JSON.parse(localStorage.getItem(cacheKey))
    return cached && Array.isArray(cached.messages) &&
      Number.isFinite(cached.totalCount) && Number.isFinite(cached.unreadCount) &&
      Number.isFinite(cached.fetchedAt) ? cached : null
  } catch (error) {
    return null
  }
}

const writeCache = state => {
  if (!state.cacheKey) {
    return
  }
  try {
    localStorage.setItem(state.cacheKey, JSON.stringify({
      messages    : state.messages.slice(0, 10),
      totalCount  : state.totalCount,
      unreadCount : state.unreadCount,
      fetchedAt   : state.fetchedAt
    }))
  } catch (error) {
    // Notifications still work when browser storage is unavailable or full.
  }
}

const refreshNotifications = async ({ state, commit, rootState }, { force = false } = {}) => {
  const cacheKey = state.cacheKey
  if (!cacheKey) {
    return
  }
  if (inFlight && inFlight.cacheKey === cacheKey) {
    if (force) {
      queuedRefreshKey = cacheKey
    }
    return inFlight.promise
  }
  if (!force && state.fetchedAt && Date.now() - state.fetchedAt < CACHE_TTL) {
    return
  }

  const userId = rootState.app.User.id
  const companyId = rootState.app.Company.id

  if (cacheKey !== `${CACHE_PREFIX}:${companyId}:${userId}`) {
    return
  }

  const revision = state.revision
  commit(types.GET_NOTIFICATIONS_BEGIN)
  const promise = (async () => {
    try {
      const { data } = await api.user.getNotifications(userId)

      if (state.cacheKey !== cacheKey) {
        return
      }
      if (state.revision !== revision) {
        commit(types.GET_NOTIFICATIONS_SUCCESS, null)
        return
      }

      commit(types.GET_NOTIFICATIONS_SUCCESS, {
        messages    : data.messages || [],
        totalCount  : data.totalMessageCount || 0,
        unreadCount : data.unreadMeesageCount || 0,
        fetchedAt   : Date.now()
      })
      writeCache(state)
    } catch (error) {
      if (state.cacheKey === cacheKey) {
        commit(types.GET_NOTIFICATIONS_ERROR, error)
      }
    }
  })()
  inFlight = { cacheKey, promise }
  try {
    await promise
  } finally {
    if (inFlight && inFlight.promise === promise) {
      inFlight = null
    }
  }
  if (queuedRefreshKey === cacheKey && state.cacheKey === cacheKey) {
    queuedRefreshKey = null
    return refreshNotifications({ state, commit, rootState }, { force : true })
  }
}

export default {
  initialize({ state, commit, rootState }, { userId, companyId }) {
    if (!userId || !companyId) {
      return
    }
    const cacheKey = `${CACHE_PREFIX}:${companyId}:${userId}`
    if (state.cacheKey !== cacheKey) {
      queuedRefreshKey = null
      commit(types.HYDRATE_NOTIFICATIONS, { cacheKey, cached : readCache(cacheKey) })
    }
    return refreshNotifications({ state, commit, rootState })
  },
  syncFromStorage({ state, commit }) {
    if (!state.cacheKey) {
      return
    }
    const cached = readCache(state.cacheKey)
    if (cached) {
      commit(types.HYDRATE_NOTIFICATIONS, { cacheKey : state.cacheKey, cached })
    }
  },
  refresh(context, options) {
    return refreshNotifications(context, options)
  },
  async markRead({ state, commit }, messageId) {
    const cacheKey = state.cacheKey
    const message = state.messages.find(item => item.id === messageId)
    if (!cacheKey || !message || message.isSeen) {
      return
    }
    commit(types.MARK_NOTIFICATION_READ_BEGIN, messageId)
    try {
      const response = await gqlClient.query({
        mutation  : messageRead,
        variables : { messageId }
      }, { timeout : 60000 })

      const updatedMessage = response.data.data.messageRead
      if (!updatedMessage || !updatedMessage.MessageReads.length) {
        throw new Error('Could not mark notification as read')
      }
      if (state.cacheKey === cacheKey) {
        commit(types.MARK_NOTIFICATION_READ_SUCCESS, updatedMessage)
        writeCache(state)
      }
    } catch (error) {
      if (state.cacheKey === cacheKey) {
        commit(types.MARK_NOTIFICATION_READ_ERROR, { messageId, error })
      }
    }
  },
  syncRead({ state, commit }, message) {
    if (!state.cacheKey || !message || !message.MessageReads || !message.MessageReads.length) {
      return
    }
    commit(types.MARK_NOTIFICATION_READ_SUCCESS, message)
    writeCache(state)
  },
  clearCache({ state, commit }) {
    queuedRefreshKey = null
    if (state.cacheKey) {
      try {
        localStorage.removeItem(state.cacheKey)
      } catch (error) {
        // The browser may have disabled storage.
      }
    }
    commit(types.HYDRATE_NOTIFICATIONS, { cacheKey : null, cached : null })
  }
}
